import { useState } from 'react';

interface ContactModalProps {
  onClose: () => void;
}

export default function ContactModal({ onClose }: ContactModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    division: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.message.length > 500) {
      setSubmitStatus('Message must be 500 characters or less.');
      return;
    }

    try {
      const formDataEncoded = new URLSearchParams();
      formDataEncoded.append('name', formData.name);
      formDataEncoded.append('email', formData.email);
      formDataEncoded.append('company', formData.company);
      formDataEncoded.append('division', formData.division);
      formDataEncoded.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6m40505dsf7sr1n6630', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataEncoded.toString(),
      });

      if (response.ok) {
        setSubmitStatus('Thank you! We will contact you soon.');
        setFormData({ name: '', email: '', company: '', division: '', message: '' });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('Submission failed. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('An error occurred. Please try again.');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between">
          <h2 className="text-3xl font-bold text-[#002B5C]">Contact Us</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-2xl text-[#002B5C]"></i>
          </button>
        </div>

        <form
          id="contact-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="p-8 space-y-6"
        >
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-[#002B5C] mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC107] text-sm"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#002B5C] mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC107] text-sm"
            />
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-medium text-[#002B5C] mb-2">
              Company/Institution
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC107] text-sm"
            />
          </div>

          <div>
            <label htmlFor="division" className="block text-sm font-medium text-[#002B5C] mb-2">
              Division of Interest *
            </label>
            <select
              id="division"
              name="division"
              value={formData.division}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC107] text-sm cursor-pointer"
            >
              <option value="">Select a division</option>
              <option value="InovaBioTech">InovaBioTech - Medical Devices</option>
              <option value="InovaBioChem">InovaBioChem - Pharmaceuticals</option>
              <option value="InovaBioLabs">InovaBioLabs - Research Labs</option>
              <option value="General">General Inquiry</option>
            </select>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-[#002B5C] mb-2">
              Message * (Max 500 characters)
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              maxLength={500}
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC107] resize-none text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.message.length}/500 characters
            </p>
          </div>

          {submitStatus && (
            <div
              className={`p-4 rounded-lg text-sm ${
                submitStatus.includes('Thank you')
                  ? 'bg-green-50 text-green-800'
                  : 'bg-red-50 text-red-800'
              }`}
            >
              {submitStatus}
            </div>
          )}

          <button
            type="submit"
            className="w-full px-8 py-4 bg-[#FFC107] text-[#002B5C] rounded-lg text-base font-semibold hover:bg-[#FF8F00] transition-colors whitespace-nowrap cursor-pointer"
          >
            Send Message
          </button>
        </form>
      </div>
    </div>
  );
}